/**
 * EffectsSystem.jsx
 * 
 * Phase 2: Particle effects and visual feedback system
 * Handles hit sparks, impact effects, and other visual feedback
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';

// Particle component
const Particle = ({ x, y, vx, vy, color, size, life, type }) => {
  const [opacity, setOpacity] = useState(1);
  const [position, setPosition] = useState({ x, y });

  useEffect(() => {
    let startTime = performance.now();
    let animationId;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = elapsed / life;

      if (progress >= 1) {
        setOpacity(0);
        return;
      }

      // Update position based on velocity
      const newX = x + vx * (elapsed / 16);
      const newY = y + vy * (elapsed / 16) + 0.5 * 0.5 * Math.pow(elapsed / 16, 2); // Add gravity
      
      setPosition({ x: newX, y: newY });
      setOpacity(1 - progress);

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [x, y, vx, vy, life]);

  if (opacity <= 0) return null;

  const style = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: size,
    height: size,
    backgroundColor: color,
    borderRadius: type === 'spark' ? '50%' : '2px',
    opacity,
    transform: 'translate(-50%, -50%)',
    pointerEvents: 'none',
  };

  return <div style={style} />;
};

// Impact effect component
const ImpactEffect = ({ x, y, color, size, angle }) => {
  const [scale, setScale] = useState(0.5);
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    let startTime = performance.now();
    const duration = 300;
    let animationId;

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = elapsed / duration;

      if (progress >= 1) {
        setOpacity(0);
        return;
      }

      // Expand then fade
      const currentScale = 0.5 + progress * 1.5;
      setScale(currentScale);
      setOpacity(1 - progress);

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  if (opacity <= 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: size * 2,
        height: size * 2,
        transform: `translate(-50%, -50%) rotate(${angle}rad) scale(${scale})`,
        opacity,
        pointerEvents: 'none',
      }}
    >
      {/* Main impact circle */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          border: `3px solid ${color}`,
          borderRadius: '50%',
          boxShadow: `0 0 20px ${color}, inset 0 0 20px ${color}40`,
        }}
      />
      {/* Cross lines */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '10%',
          right: '10%',
          height: '2px',
          backgroundColor: color,
          transform: 'translateY(-50%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '10%',
          bottom: '10%',
          width: '2px',
          backgroundColor: color,
          transform: 'translateX(-50%)',
        }}
      />
    </div>
  );
};

export const EffectsSystem = React.forwardRef(({
  width = 1280,
  height = 720,
}, ref) => {
  const [particles, setParticles] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const particleIdRef = useRef(0);
  const impactIdRef = useRef(0);

  // Expose methods to parent
  React.useImperativeHandle(ref, () => ({
    /**
     * Spawn hit particles at position
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} count - Number of particles
     * @param {string} color - Particle color
     * @param {number} force - Particle velocity multiplier
     */
    spawnHitParticles: (x, y, count = 8, color = '#ffffff', force = 1) => {
      const newParticles = [];
      const id = particleIdRef.current++;

      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
        const speed = (3 + Math.random() * 5) * force;
        
        newParticles.push({
          id: `${id}-${i}`,
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color,
          size: 4 + Math.random() * 4,
          life: 300 + Math.random() * 200,
          type: 'spark',
        });
      }

      setParticles(prev => [...prev, ...newParticles]);

      // Clean up old particles after they've lived
      setTimeout(() => {
        setParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
      }, 600);
    },

    /**
     * Spawn impact effect
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {string} color - Effect color
     * @param {number} size - Effect size
     * @param {number} angle - Effect rotation angle
     */
    spawnImpact: (x, y, color = '#ffffff', size = 30, angle = 0) => {
      const id = impactIdRef.current++;
      const impact = { id, x, y, color, size, angle };
      
      setImpacts(prev => [...prev, impact]);

      // Remove after animation
      setTimeout(() => {
        setImpacts(prev => prev.filter(i => i.id !== id));
      }, 350);
    },

    /**
     * Clear all effects
     */
    clear: () => {
      setParticles([]);
      setImpacts([]);
    },
  }));

  return (
    <div
      className="effects-system"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Particles */}
      {particles.map(particle => (
        <Particle key={particle.id} {...particle} />
      ))}

      {/* Impact effects */}
      {impacts.map(impact => (
        <ImpactEffect key={impact.id} {...impact} />
      ))}
    </div>
  );
});

EffectsSystem.displayName = 'EffectsSystem';

export default EffectsSystem;
